/**
 * Packaging OS — Supplier brief / RFQ page (Phase 26).
 * POST /api/workflows/supplier-rfq/dry-run.
 */

import { useState, type FormEvent } from "react";
import {
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  PRICE_BANDS,
  PRINT_FINISHES,
  QUANTITY_RANGES,
  SIZE_UNITS,
  runSupplierRfqDryRun,
  type ApiResult,
  type DryRunChannel,
  type DryRunLanguage,
  type PriceBand,
  type PrintFinish,
  type QuantityRange,
  type SizeUnit,
  type SupplierRfqRequest,
  type SupplierRfqResponse,
  runWorkflowPersistedFirst,
} from "../api/client";
import { AIAssistToggle } from "../components/AIAssistToggle";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  category: string;
  brand_name: string;
  target_market: string;
  packaging_format: string;
  packaging_material: string;
  structure_recommendation: string;
  size_value: string;
  size_unit: string;
  quantity_range: QuantityRange;
  price_band: PriceBand;
  print_finish: PrintFinish;
  sales_channel: string;
  sustainability_preferences: string;
  known_constraints: string;
  questions_for_supplier: string;
  notes: string;
  language: DryRunLanguage;
}

const EMPTY: FormState = {
  product_name: "",
  category: "",
  brand_name: "",
  target_market: "",
  packaging_format: "",
  packaging_material: "",
  structure_recommendation: "",
  size_value: "",
  size_unit: "",
  quantity_range: "unknown",
  price_band: "unknown",
  print_finish: "none",
  sales_channel: "",
  sustainability_preferences: "",
  known_constraints: "",
  questions_for_supplier: "",
  notes: "",
  language: "en",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<SupplierRfqResponse> };

interface FieldErrors {
  product_name?: string;
  category?: string;
  brand_name?: string;
  target_market?: string;
  packaging_format?: string;
  packaging_material?: string;
}

function splitLines(s: string, max: number): string[] {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .slice(0, max);
}

export function SupplierRfqPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale
      : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [aiAssist, setAiAssist] = useState<boolean>(false);

  function update<K extends keyof FormState>(k: K, v: FormState[K]): void {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    const r = t.workflowDryRunRequiredHint;
    if (!form.product_name.trim()) e.product_name = r;
    if (!form.category.trim()) e.category = r;
    if (!form.brand_name.trim()) e.brand_name = r;
    if (!form.target_market.trim()) e.target_market = r;
    if (!form.packaging_format.trim()) e.packaging_format = r;
    if (!form.packaging_material.trim()) e.packaging_material = r;
    return e;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const sizeNum = form.size_value.trim() ? Number(form.size_value) : null;
    const body: SupplierRfqRequest = {
      product_name: form.product_name.trim(),
      category: form.category.trim(),
      brand_name: form.brand_name.trim(),
      target_market: form.target_market.trim().toUpperCase(),
      packaging_format: form.packaging_format.trim(),
      packaging_material: form.packaging_material.trim(),
      structure_recommendation: form.structure_recommendation.trim() || null,
      size_value: sizeNum !== null && Number.isFinite(sizeNum) ? sizeNum : null,
      size_unit: form.size_unit ? (form.size_unit as SizeUnit) : null,
      quantity_range: form.quantity_range,
      price_band: form.price_band,
      print_finish: form.print_finish,
      sales_channel: form.sales_channel
        ? (form.sales_channel as DryRunChannel)
        : null,
      sustainability_preferences: splitLines(form.sustainability_preferences, 8),
      known_constraints: form.known_constraints.trim() || null,
      questions_for_supplier: splitLines(form.questions_for_supplier, 12),
      notes: form.notes.trim() || null,
      language: form.language,
      ai_assist: aiAssist,
    };
    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("supplier-rfq", body, runSupplierRfqDryRun);
    setState({ kind: "loaded", result });
  }

  function onReset(): void {
    setForm({
      ...EMPTY,
      language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
        ? locale
        : "en") as DryRunLanguage,
    });
    setErrors({});
    setState({ kind: "idle" });
  }

  const opt = (vals: readonly string[]) =>
    vals.map((v) => ({ value: v, label: v }));

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">
          {t.appShellNavWorkflows}
        </span>
        <h1 className="po-section-header__title">{t.supplierTitle}</h1>
        <p className="po-note">{t.supplierSubtitle}</p>
      </header>

      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField
              label={t.intakeFieldProductName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name}
              onChange={(v) => update("product_name", v)}
              error={errors.product_name}
              maxLength={200}
            />
            <TextField
              label={t.intakeFieldProductCategory}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.category}
              onChange={(v) => update("category", v)}
              error={errors.category}
              maxLength={100}
            />
            <TextField
              label={t.intakeFieldBrandName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.brand_name}
              onChange={(v) => update("brand_name", v)}
              error={errors.brand_name}
              maxLength={200}
            />
            <TextField
              label={t.labelCopyFieldTargetMarket}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.target_market}
              onChange={(v) => update("target_market", v.toUpperCase())}
              error={errors.target_market}
              maxLength={8}
            />
            <TextField
              label={t.supplierFieldFormat}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.packaging_format}
              onChange={(v) => update("packaging_format", v)}
              error={errors.packaging_format}
              maxLength={120}
            />
            <TextField
              label={t.supplierFieldMaterial}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.packaging_material}
              onChange={(v) => update("packaging_material", v)}
              error={errors.packaging_material}
              maxLength={120}
            />
            <TextField
              label={t.supplierFieldStructure}
              value={form.structure_recommendation}
              onChange={(v) => update("structure_recommendation", v)}
              maxLength={500}
            />
            <TextField
              label={t.intakeFieldSizeValue}
              value={form.size_value}
              onChange={(v) => update("size_value", v)}
            />
            <SelectField
              label={t.intakeFieldSizeUnit}
              value={form.size_unit}
              onChange={(v) => update("size_unit", v)}
              options={opt(SIZE_UNITS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.supplierFieldQuantity}
              value={form.quantity_range}
              onChange={(v) => update("quantity_range", v as QuantityRange)}
              options={opt(QUANTITY_RANGES)}
            />
            <SelectField
              label={t.intakeFieldPriceBand}
              value={form.price_band}
              onChange={(v) => update("price_band", v as PriceBand)}
              options={opt(PRICE_BANDS)}
            />
            <SelectField
              label={t.supplierFieldPrintFinish}
              value={form.print_finish}
              onChange={(v) => update("print_finish", v as PrintFinish)}
              options={opt(PRINT_FINISHES)}
            />
            <SelectField
              label={t.labelCopyFieldSalesChannel}
              value={form.sales_channel}
              onChange={(v) => update("sales_channel", v)}
              options={opt(DRY_RUN_CHANNELS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.intakeFieldLanguage}
              value={form.language}
              onChange={(v) => update("language", v as DryRunLanguage)}
              options={opt(DRY_RUN_LANGUAGES)}
            />
            <TextAreaField
              label={t.materialFieldSustainabilityPreferences}
              hint={t.intakeFieldSustainabilityPreferencesHelp}
              value={form.sustainability_preferences}
              onChange={(v) => update("sustainability_preferences", v)}
              rows={3}
              full
            />
            <TextAreaField
              label={t.supplierFieldKnownConstraints}
              value={form.known_constraints}
              onChange={(v) => update("known_constraints", v)}
              rows={2}
              maxLength={500}
              full
            />
            <TextAreaField
              label={t.supplierFieldQuestions}
              hint={t.supplierFieldQuestionsHelp}
              value={form.questions_for_supplier}
              onChange={(v) => update("questions_for_supplier", v)}
              rows={3}
              full
            />
            <TextAreaField
              label={t.intakeFieldNotes}
              value={form.notes}
              onChange={(v) => update("notes", v)}
              rows={2}
              maxLength={1000}
              full
            />
          </FieldGrid>

          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">
              {t.workflowDryRunValidationFailed}
            </p>
          )}

          <AIAssistToggle
            checked={aiAssist}
            onChange={setAiAssist}
            label={t.aiAssistToggleLabel}
            hint={t.aiAssistToggleHint}
            banner={t.aiAssistDraftBanner}
            disabled={state.kind === "submitting"}
          />

          <div className="po-row">
            <Button
              variant="primary"
              type="submit"
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t.supplierSubmitting
                : t.supplierSubmit}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={onReset}
              disabled={state.kind === "submitting"}
            >
              {t.supplierReset}
            </Button>
          </div>
        </form>
      </section>

      {state.kind === "loaded" && (
        <DataState
          state={{ kind: "loaded", result: state.result }}
          t={t}
          renderOk={(data, requestId) => (
            <SupplierReport data={data} requestId={requestId} t={t} />
          )}
        />
      )}
    </AppLayout>
  );
}

interface SupplierReportProps {
  data: SupplierRfqResponse;
  requestId: string | null;
  t: UiStrings;
}

function SupplierReport({
  data,
  requestId,
  t,
}: SupplierReportProps): JSX.Element {
  return (
    <div className="po-stack" data-testid="po-supplier-rfq-report">
      <div className="po-halt-banner po-halt-banner--medium" role="alert">
        {t.supplierSubtitle}
      </div>
      <ReportSection title={t.supplierReportBrief}>
        <BulletList items={data.supplier_brief_summary} />
      </ReportSection>
      <ReportSection title={t.supplierReportSpec}>
        <BulletList items={data.packaging_specification_draft} />
      </ReportSection>
      <ReportSection title={t.supplierReportMaterial}>
        <BulletList items={data.material_structure_requirements} />
      </ReportSection>
      <ReportSection title={t.supplierReportPrint}>
        <BulletList items={data.print_finish_requirements} />
      </ReportSection>
      <ReportSection title={t.supplierReportQuantity}>
        <BulletList items={data.quantity_questions} />
      </ReportSection>
      <ReportSection title={t.supplierReportLead}>
        <BulletList items={data.lead_time_questions} />
      </ReportSection>
      <ReportSection title={t.supplierReportQuality}>
        <BulletList items={data.quality_testing_questions} />
      </ReportSection>
      <ReportSection title={t.supplierReportSust}>
        <BulletList
          items={data.sustainability_evidence_questions}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.supplierReportRfqEmail}>
        <BulletList items={data.rfq_email_draft} />
      </ReportSection>
      <ReportSection title={t.supplierReportCompare}>
        <BulletList items={data.supplier_comparison_criteria} />
      </ReportSection>
      <ReportSection title={t.supplierReportMissing}>
        <BulletList
          items={data.missing_production_information}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.intakeReportHumanReview}>
        <BulletList items={data.human_review_required_notes} />
      </ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
